/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

/**
 * @typedef {import('App/Pages/Project/IdpApplication/idp-project/idp-extraction-project/structured-extraction/extracted-results/types').TestDataSummary} TestDataSummary
 * @typedef {import('App/Pages/Project/IdpApplication/idp-project/idp-extraction-project/structured-extraction/extracted-results/types').TestSummaryDetailedResults} TestSummaryDetailedResults
 * @typedef {import('App/Pages/Project/IdpApplication/idp-project/idp-extraction-project/structured-extraction/extracted-results/types').StructuredExtractionResults} StructuredExtractionResults
 *
 */

import { useRef, useState } from 'react';
import { FileUploaderButton, Stack, InlineLoading } from '@carbon/react';
import { spacing03 } from '@carbon/layout';
import { Upload } from '@carbon/icons-react';

import { ALLOWED_IDP_DOCUMENT_FILE_TYPES } from 'App/Pages/Project/IdpApplication/idp-project/utils/constants';

import {
  TestDataSummary,
  TestDataSummaryHeader,
  TestDataSummarySection,
  HeaderWithUploadContainer
} from './TestTemplateOverviewResults.styled';
import { TestSummaryTiles } from './TestSummaryTiles';
import { TestResultSidePanel } from './test-result-side-panel/TestResultSidePanel';
import { DetailedResultsTable } from './DetailedResultsTable';

/**
 * @param {Object} props
 * @param {TestDataSummary} props.data
 * @param {Array} props.documents
 * @param {StructuredExtractionResults} props.templateData
 * @param {(documentId: string) => void} props.onDeleteDocument - Callback to delete a document
 * @param {(files: FileList) => void} props.onAddDocuments - Callback to add documents
 * @param {boolean} props.isProcessingDocuments - Whether test template operations are in progress
 * @property {string} props.documents[].id - Document ID
 * @property {string} props.documents[].name - Document filename
 * @returns {JSX.Element}
 */
export function TestTemplateOverviewResults({
  data,
  documents,
  templateData,
  onDeleteDocument,
  onAddDocuments,
  isProcessingDocuments
}) {
  const sidePanelRef = useRef(null);
  const [selectedView, setSelectedView] = useState(/** @type {'forms'|'tables'} */ ('forms'));
  const [selectedDocumentData, setSelectedDocumentData] = useState(
    /** @type {TestSummaryDetailedResults|null} */ (null)
  );
  const [selectedDocumentIndex, setSelectedDocumentIndex] = useState(/** @type {number|null} */ (null));

  const handleViewChange = ({ name }) => {
    setSelectedView(name);
  };

  /**
   * @param {TestSummaryDetailedResults} detailData
   * @param {number} documentIndex
   */
  const handleViewExtraction = (detailData, documentIndex) => {
    setSelectedDocumentData(detailData);
    setSelectedDocumentIndex(documentIndex);
    sidePanelRef.current?.open();
  };

  if (!data || !documents?.length) {
    return null;
  }

  return (
    <>
      <TestDataSummary>
        <TestDataSummarySection>
          <TestDataSummaryHeader>Test Summary Results</TestDataSummaryHeader>
          <TestSummaryTiles testSummaryResults={data.testSummaryResults} />
        </TestDataSummarySection>

        <TestDataSummarySection>
          <HeaderWithUploadContainer>
            <TestDataSummaryHeader>Detailed Results</TestDataSummaryHeader>
            {isProcessingDocuments ? (
              <span>
                <InlineLoading
                  status="active"
                  iconDescription="Extracting documents..."
                  description="Extracting documents..."
                />
              </span>
            ) : (
              <FileUploaderButton
                name="idp-form-documents"
                buttonKind="ghost"
                accept={ALLOWED_IDP_DOCUMENT_FILE_TYPES}
                multiple
                onChange={(event) => onAddDocuments(event.target.files)}
                labelText={
                  <Stack gap={spacing03} orientation="horizontal">
                    {/* Carbon set the svg fill to be var(--cds-icon-primary, #161616) if it's a child of a "ghost" button so we need to override */}
                    Upload documents <Upload size={16} style={{ fill: 'currentColor' }} />
                  </Stack>
                }
                size="lg"
              />
            )}
          </HeaderWithUploadContainer>
          <DetailedResultsTable
            testSummaryDetailedResults={data.testSummaryDetailedResults}
            documents={documents}
            testSummaryResults={data.testSummaryResults}
            onViewExtraction={handleViewExtraction}
            onDeleteDocument={onDeleteDocument}
          />
        </TestDataSummarySection>
      </TestDataSummary>
      <TestResultSidePanel
        title={
          selectedDocumentIndex !== null && documents[selectedDocumentIndex]
            ? `Test Results - ${documents[selectedDocumentIndex].name}`
            : 'Test Results'
        }
        sidePanelRef={sidePanelRef}
        testSummaryResults={data.testSummaryResults}
        selectedDocumentData={selectedDocumentData}
        selectedView={selectedView}
        onViewChange={handleViewChange}
        templateData={templateData}
      />
    </>
  );
}
